import { memo, useCallback, useMemo } from 'react';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
// @ts-expect-error Untyped dependency
import PhoneNumberInput from 'ui-addon-i18n/components/PhoneNumberInput';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import styled from 'foundations-theming/utils';
import { isValidRawPhoneNumber, parseRawPhoneNumber } from './phone/utils/phoneNumberUtils';
import { Button } from 'foundations-components/transitional/components/Button';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { FETCH_STATUS } from './phone/hooks/usePhoneNumberValidator';
import { Tile, TileSection } from 'foundations-components/transitional/components/Tile';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { InfoIcon } from 'foundations-assets';
import { View } from 'foundations-components/transitional/components/View';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledInfoIconWrapper = styled.span.withConfig({
  displayName: "PropertyInputPhone__StyledInfoIconWrapper",
  componentId: "sc-3eq567-0"
})(["margin-inline-start:", ";"], ({
  theme
}) => theme.space['200']);
export const getPhoneNumberString = (phoneNumber, extension) => extension !== null && extension !== '' && extension !== undefined ? `${phoneNumber} ext ${extension}` : phoneNumber || '';
const PropertyInputPhone = ({
  disabled,
  value,
  initialCountryDialCode,
  status,
  error,
  showTextInput,
  onChange,
  onToggleInput
}) => {
  const {
    rawValue,
    extensionValue
  } = useMemo(() => {
    if (isValidRawPhoneNumber(value)) {
      const {
        phoneNumber,
        extension
      } = parseRawPhoneNumber(value);
      return {
        rawValue: phoneNumber,
        extensionValue: extension
      };
    }
    return {
      rawValue: value,
      extensionValue: ''
    };
  }, [value]);
  const handleTextChange = useCallback(evt => {
    onChange(evt.target.value);
  }, [onChange]);
  const handlePhoneChange = useCallback(({
    phoneNumber,
    extension,
    country
  }) => {
    const newPhoneNumber = getPhoneNumberString(phoneNumber, extension);
    onChange(newPhoneNumber, country);
  }, [onChange]);
  const renderErrorMessage = useMemo(() => {
    const isPending = status === FETCH_STATUS.PENDING;
    const isSucceeded = status === FETCH_STATUS.SUCCEEDED;
    if (value === null || value === undefined || value === '' || value === initialCountryDialCode) {
      return /*#__PURE__*/_jsx(Text, {
        as: "span",
        color: "do-not-use-slinky",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.PropertyInputPhone.phoneNumberValidations.prevalidation",
          "data-unit-test": "prevalidation-message"
        })
      });
    }
    if ((isPending || isSucceeded) && error) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.PropertyInputPhone.phoneNumberErrors.numberInvalid",
        "data-unit-test": "error-number-invalid"
      });
    }
    if (error) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.PropertyInputPhone.phoneNumberErrors.validationFailed"
      });
    }
    if (isPending) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.PropertyInputPhone.phoneNumberValidations.validating"
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customerDataProperties.PropertyInputPhone.phoneNumberValidations.validated"
    });
  }, [status, error, value, initialCountryDialCode]);
  const message = showTextInput ? 'customerDataProperties.PropertyInputPhone.editOptions.editAsPhoneNumber.btn' : 'customerDataProperties.PropertyInputPhone.editOptions.editAsText.btn';
  const tooltip = showTextInput ? 'customerDataProperties.PropertyInputPhone.editOptions.editAsPhoneNumber.tooltip' : 'customerDataProperties.PropertyInputPhone.editOptions.editAsText.tooltip';
  return /*#__PURE__*/_jsxs(View, {
    minInlineSize: "359px",
    children: [showTextInput ? /*#__PURE__*/_jsx(TextInput, {
      disabled: disabled,
      value: value,
      onChange: handleTextChange,
      autoFocus: true
    }) : /*#__PURE__*/_jsx(Tile, {
      compact: true,
      children: /*#__PURE__*/_jsx(TileSection, {
        paddingInline: "250",
        paddingBlockStart: "300",
        paddingBlockEnd: "250",
        children: /*#__PURE__*/_jsx(FormControl, {
          error: error,
          validationMessage: renderErrorMessage,
          "aria-label": unescapedText('customerDataProperties.PropertyInputPhone.ariaLabel'),
          children: /*#__PURE__*/_jsx(Flex, {
            children: /*#__PURE__*/_jsx(PhoneNumberInput, {
              value: rawValue || initialCountryDialCode,
              extensionValue: extensionValue,
              phoneInputProps: {
                disabled,
                autoFocus: true
              },
              onChange: handlePhoneChange,
              countrySelectProps: {
                disabled
              },
              extensionInputProps: {
                disabled
              }
            })
          })
        })
      })
    }), /*#__PURE__*/_jsxs(View, {
      marginBlockStart: "300",
      children: [/*#__PURE__*/_jsx(Button, {
        use: "link",
        onClick: onToggleInput,
        "data-selenium-test": "toggle-input-type-button",
        disabled: status === FETCH_STATUS.PENDING,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: message
        })
      }), /*#__PURE__*/_jsx(Tooltip, {
        placement: "bottom",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: tooltip
        }),
        children: /*#__PURE__*/_jsx(StyledInfoIconWrapper, {
          children: /*#__PURE__*/_jsx(InfoIcon, {
            alignWithText: true,
            color: 'icon-core-subtle'
          })
        })
      })]
    })]
  });
};
export default /*#__PURE__*/memo(PropertyInputPhone);